"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Gateway = exports.BackhaulTypes = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const ModelClassInterfaces_1 = require("../../ModelClassInterfaces");
const constants_1 = require("../../constants");
const types_1 = require("../../types");
const millisecondOperations_1 = require("../../util/time/millisecondOperations");
const GeoModelCommon_1 = require("./GeoModelCommon");
const iotv_v3_types_2 = require("@iotv/iotv-v3-types");
const typeName = 'Gateway';
var BackhaulTypes;
(function (BackhaulTypes) {
    BackhaulTypes["WiFi"] = "WiFi";
    BackhaulTypes["Ethernet"] = "Ethernet";
    BackhaulTypes["4G"] = "4G";
})(BackhaulTypes || (exports.BackhaulTypes = BackhaulTypes = {}));
class Gateway extends GeoModelCommon_1.GeoModelCommon {
    constructor(ob) {
        super({ ...ob, type: ob.type ?? typeName });
    }
    getStateKeys() {
        let stateAttKeys = ['primaryGatewayUplinkCount', 'secondaryGatewayReceiveCount'];
        return stateAttKeys;
    }
    getUserAttributes() {
        return {
            ...super.getUserAttributes(),
            name: { type: 'String', required: true, editable: true },
            eui: {
                type: 'String',
                required: true,
                editable: false,
                label: 'Gateway EUI',
                name: 'eui',
                validation: { regex: iotv_v3_types_1.CommonRegexMap.LoRa.eui, pass: true, invalidMessage: 'Gateway EUI must be 8 hexadecimal bytes' },
                valueFn: (newValue) => (newValue ? newValue.toUpperCase().replace(/\s/g, '') : ''),
                displayFn: (val) => {
                    return val?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ')?.toUpperCase() ?? '';
                },
            },
            networkIP: { name: 'networkIP', type: 'String', editable: false, label: 'NW IP' },
            networkKey: { name: 'networkKey', type: 'Enum', editable: false, enumKv: iotv_v3_types_1.Networks, label: 'NW' },
            lncInstanceType: {
                name: 'lncInstanceType', type: 'Enum', editable: true, enumKv: { LOCAL: 'LOCAL', LNC: 'LNC' }, label: 'LNC/LOCAL', default: iotv_v3_types_2.RFRegions['AS923-1'],
                dependsOn: (item) => {
                    const partialVKD = item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN ? {
                        required: item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN,
                        editable: !item.lncInstanceType || (item.eui && item.eui !== item.id) // the second part of this uses a sideeffect to determine whether the item is new and the user should be allowed to change the lnsIntanceType
                    } : { hidden: true };
                    return partialVKD;
                }
            },
            regionalPlan: {
                name: 'regionalPlan', type: 'Enum', required: true, editable: false, enumKv: iotv_v3_types_2.RFRegions, label: 'RFRegion',
                dependsOn: (item) => {
                    const partialVKD = item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN ? {
                        required: item.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN,
                        editable: !item.regionalPlan
                    } : {};
                    return partialVKD;
                }
            },
            lastSeen: {
                type: 'Date',
                editable: false,
                label: 'Last Seen',
                name: 'lastSeen',
                precision: types_1.UserAttributeKeyDatePrecision.DateTimeSecs,
            },
            state: { type: 'Enum', editable: true, label: 'State', name: 'state', required: true, enumKv: ModelClassInterfaces_1.GatewayState },
            lastDeviceSeen: { type: 'String', editable: false, label: 'lastDeviceSeen', name: 'lastDeviceSeen' },
            model: { type: 'String', editable: true, label: 'Model', name: 'model' },
            manufacturer: { type: 'String', editable: true, label: 'Manufacturer', name: 'manufacturer' },
            serialNumber: { type: 'String', editable: true, label: 'Serial Number', name: 'serialNumber' },
            backhaul: {
                type: 'Enum',
                editable: true,
                label: 'Backhaul Type',
                name: 'backhaul',
                enumKv: BackhaulTypes,
            },
            lastAWOL: {
                type: 'Number',
                editable: false,
                label: 'Last AWOL',
                name: 'Last AWOL',
                displayFn: () => this.AWOLHistory && this.AWOLHistory[this.AWOLHistory.length - 1],
                valueFn: () => this.AWOLHistory && this.AWOLHistory[this.AWOLHistory.length - 1],
            },
            AWOLHistory: {
                type: 'Number',
                editable: false,
                label: 'AWOL History',
                name: 'AWOL History',
                displayFn: () => this.AWOLHistory && [...this.AWOLHistory].reverse().join(', '),
                valueFn: () => this.AWOLHistory && [...this.AWOLHistory].reverse().join(', '),
            },
            installDate: {
                type: 'Date',
                editable: true,
                label: 'Install Date',
                name: 'installDate',
                displayFn: (actualValue) => (actualValue ? (0, millisecondOperations_1.toDatePickerFormat)(actualValue) : undefined),
                valueFn: (newValue) => (newValue ? new Date(newValue).valueOf() : undefined),
            },
            ports: { type: 'Number', editable: true, label: 'Ports', name: 'ports' },
            lastSendPeriod: { type: 'Number', editable: false, label: 'Last Send Period', name: 'Last Send Period' },
            maxAirTimeUtilization: { type: 'Number', editable: true, units: '%', label: 'Congestion Limit', name: 'Congestion Limit', description: 'Percentage airtime utilized before considered congested' },
            airTimeUtilization: { type: 'Number', editable: false, label: 'Airtime Utilisation', name: 'Last Send Period' },
            congested: { type: 'Boolean', editable: false, label: 'Is Congested Now', name: 'Is Congested Now' },
            congesting: { type: 'Boolean', editable: false, label: 'Has shown congestion', name: 'Has shown congestion' },
            primaryGatewayUplinkCount: { type: 'Number', editable: false, label: 'Primary Receives', name: 'primaryGatewayUplinkCount' },
            secondaryGatewayReceiveCount: { type: 'Number', editable: false, label: 'Secondary Receives', name: 'secondaryGatewayReceiveCount' },
            // primaryDevices: { type: 'Number', editable: false, label: 'Primary Devices', valueFn()},
            channels: { type: 'Number', editable: true, label: 'Channels', name: 'Channels' },
            frequency: { type: 'Number', editable: true, label: 'Frequency', name: 'frequency' },
            isAWOL: { type: 'Boolean', editable: false, label: 'Is AWOL?', name: 'isAWOL' },
            _permittedAWOL: {
                type: 'Number',
                editable: true,
                label: 'Max AWOL (mins)',
                name: '_permittedAWOL',
                displayFn: (actualValue) => (actualValue ? actualValue / constants_1.Milliseconds.MINUTE : undefined),
                valueFn: (newValue) => (newValue ? newValue * constants_1.Milliseconds.MINUTE : undefined),
            },
            remoteEnabled: { type: 'Boolean', editable: true, label: 'Remote Enabled', name: 'Remote Enabled' },
            firmwareVersion: { type: 'String', editable: true, label: 'Firmware Version', name: 'Firmware Version' },
            GWUserID: { type: 'String', editable: true, label: 'GW User ID', name: 'GW User ID' },
            GWUserPW: { type: 'String', editable: true, label: 'GW Password', name: 'GW Password' },
            LastUpdate: { type: 'String', editable: true, label: 'Last Updated', name: 'LastUpdate' },
            WAPESSID: { type: 'String', editable: true, label: 'WAP ESSID', name: 'WAP ESSID' },
            modemModel: { type: 'String', editable: true, label: 'Modem Model', name: 'Modem Model' },
            antennaModel: { type: 'String', editable: true, label: 'Antenna Model', name: 'Antenna Model' },
            antennaCable: { type: 'String', editable: true, label: 'Antenna Cable', name: 'Antenna Cable' },
            modemSerialNumber: { type: 'String', editable: true, label: 'Modem Serial Number', name: 'Modem Serial Number' },
            SIMNumber: { type: 'String', editable: true, label: 'SIM Number', name: 'SIM Number' },
            SIMPUK: { type: 'String', editable: true, label: 'SIM PUK', name: 'SIM PUK' },
            recentDevices: {
                type: 'String',
                editable: false,
                label: 'Recent Devices',
                name: 'Recent Devices',
                displayFn: () => this.recentDevices && [...this.recentDevices].reverse().join(', '),
                valueFn: () => this.recentDevices && [...this.recentDevices].reverse().join(', '),
            },
            downlinkCapacity: { type: 'Number', editable: true, label: 'Downlink Capcity', name: 'Downlink Capcity' },
            uplinkCapcity: { type: 'Number', editable: true, label: 'Uplink Capcity', name: 'Uplink Capcity' },
        };
    }
    set permittedAWOL_MS(awolMs) {
        this._permittedAWOL = awolMs;
    }
    get permittedAWOL_MS() {
        return this._permittedAWOL;
    }
}
exports.Gateway = Gateway;
